import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, KeyRound, ShieldOff, Pencil, History, Upload } from "lucide-react";
import Modal, { Field, Input, Textarea, GoldButton, GhostButton, PageHeader, EmptyState } from "@/components/Ui";
import HistoryDrawer from "@/components/HistoryDrawer";
import BulkImportModal from "@/components/BulkImportModal";
import { fmtDate } from "@/lib/time";
import useRowKeyboardNav from "@/lib/useRowKeyboardNav";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import { PhoneInput } from "@/components/PhoneInput";

export default function Clients() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "", district: "", state: "", pincode: "", occupation: "", notes: "" });
    const [saving, setSaving] = useState(false);
    const [portalFor, setPortalFor] = useState(null);
    const [portalForm, setPortalForm] = useState({ email: "", password: "", send_invite: false });
    const [portalAccess, setPortalAccess] = useState(null);
    const [edit, setEdit] = useState(null);
    const [editSaving, setEditSaving] = useState(false);
    const [historyFor, setHistoryFor] = useState(null);
    const [showBulk, setShowBulk] = useState(false);

    const load = async () => {
        try { const { data } = await api.get("/clients"); setItems(data); }
        catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await api.post("/clients", form);
            toast.success("Client added");
            setOpen(false);
            setForm({ name: "", mobile: "", email: "", address: "", district: "", state: "", pincode: "", occupation: "", notes: "" });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const filtered = items.filter(c =>
        !q || c.name?.toLowerCase().includes(q.toLowerCase()) || c.mobile?.includes(q) || c.code?.includes(q)
    );
    const tableCtrl = useTableControls(filtered, { storageKey: "clients" });

    const openPortal = async (c) => {
        setPortalFor(c);
        setPortalAccess(null);
        setPortalForm({ email: c.email || "", password: "", send_invite: false });
        try {
            const { data } = await api.get(`/clients/${c.id}/portal-access`);
            setPortalAccess(data);
            if (data.granted) setPortalForm(p => ({ ...p, email: data.email }));
        } catch (_) { /* ignore */ }
    };

    const grantPortal = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post(`/clients/${portalFor.id}/portal-access`, portalForm);
            const baseTitle = data.rotated ? "Password rotated" : "Portal access granted";
            let description = `${portalForm.email} · password ${portalForm.password}`;
            if (portalForm.send_invite) {
                if (data.email_sent) {
                    description += " · email sent ✓";
                    toast.success(baseTitle, { description });
                } else {
                    toast.warning(`${baseTitle} — but email NOT sent`, {
                        description: data.email_error
                            ? `Reason: ${data.email_error}`
                            : "Platform email integration is not configured. Share the credentials with the client manually for now.",
                        duration: 12000,
                    });
                }
            } else {
                toast.success(baseTitle, { description });
            }
            setPortalFor(null);
        } catch (e) { toast.error(apiError(e)); }
    };

    const revokePortal = async () => {
        if (!window.confirm("Revoke portal access for this client?")) return;
        try {
            await api.delete(`/clients/${portalFor.id}/portal-access`);
            toast.success("Portal access revoked");
            setPortalFor(null);
        } catch (e) { toast.error(apiError(e)); }
    };

    const genPassword = () => {
        const c = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
        let p = ""; for (let i = 0; i < 10; i++) p += c[Math.floor(Math.random() * c.length)];
        setPortalForm(f => ({ ...f, password: p }));
    };

    const openEdit = (c) => setEdit({
        id: c.id,
        name: c.name || "",
        mobile: c.mobile || "",
        email: c.email || "",
        occupation: c.occupation || "",
        district: c.district || "",
        state: c.state || "",
        pincode: c.pincode || "",
        address: c.address || "",
        notes: c.notes || "",
        reason: "",
        _orig: c,
    });

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!edit.reason || edit.reason.trim().length < 3) {
            toast.error("Remarks are mandatory (minimum 3 characters)"); return;
        }
        const { id, reason, _orig, ...payload } = edit;
        const diff = {};
        Object.keys(payload).forEach(k => {
            if ((payload[k] ?? "") !== (_orig[k] ?? "")) diff[k] = payload[k];
        });
        if (Object.keys(diff).length === 0) {
            toast.error("No fields changed"); return;
        }
        setEditSaving(true);
        try {
            await api.patch(`/clients/${id}`, diff, { headers: { "X-Change-Reason": reason.trim() } });
            toast.success("Client updated · logged to audit trail");
            setEdit(null);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setEditSaving(false); }
    };

    const { getRowProps } = useRowKeyboardNav({ items: filtered, onOpen: (c) => openEdit(c), disabled: !!edit || open || showBulk });

    return (
        <>
            <PageHeader
                title="Clients"
                subtitle="Every party your chambers represents — codified, tracked, retrievable."
                action={<div className="flex gap-2">
                    <GhostButton onClick={() => setShowBulk(true)} data-testid="clients-bulk-import-btn">
                        <Upload className="w-4 h-4 inline mr-2" strokeWidth={2} />Bulk import
                    </GhostButton>
                    <GoldButton onClick={() => setOpen(true)} data-testid="clients-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2} />New client</GoldButton>
                </div>}
            />

            <div className="mb-6 relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5}/>
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, mobile, code…" className="pl-11" data-testid="clients-search" />
            </div>

            {filtered.length === 0 ? (
                <EmptyState title="No clients yet" subtitle="Your client list will populate as you onboard parties." action={<GoldButton onClick={() => setOpen(true)} data-testid="clients-empty-add">Add first client</GoldButton>} />
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[640px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="code" ctrl={tableCtrl}>Code</SortableTh>
                                <SortableTh field="name" ctrl={tableCtrl}>Name</SortableTh>
                                <SortableTh field="mobile" ctrl={tableCtrl}>Mobile</SortableTh>
                                <SortableTh field="email" ctrl={tableCtrl}>Email</SortableTh>
                                <SortableTh field="district" ctrl={tableCtrl}>District</SortableTh>
                                <SortableTh field="occupation" ctrl={tableCtrl}>Occupation</SortableTh>
                                <th className="px-4 py-3 text-xs uppercase tracking-wider font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map((c, idx) => (
                                <tr key={c.id} {...getRowProps(idx)} onClick={(e) => { if ((e.target.closest('button,a'))) return; openEdit(c); }}
                                    className="border-b border-border hover:bg-muted/50 cursor-pointer" data-testid={`client-row-${c.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-primary">{c.code}</td>
                                    <td className="px-4 py-3">{c.name}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.mobile || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.district || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.occupation || "—"}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => openEdit(c)} data-testid={`client-edit-btn-${c.id}`} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mr-3"><Pencil className="w-3 h-3" strokeWidth={1.5}/>Edit</button>
                                        <button onClick={() => setHistoryFor(c)} data-testid={`client-history-btn-${c.id}`} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mr-3"><History className="w-3 h-3" strokeWidth={1.5}/>History</button>
                                        <button onClick={() => openPortal(c)} data-testid={`client-portal-btn-${c.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1"><KeyRound className="w-3 h-3" strokeWidth={1.5}/>Portal</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="New client" size="lg" testId="client-modal">
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Name"><Input required value={form.name} onChange={upd("name")} data-testid="client-name-input"/></Field>
                        <Field label="Mobile">
                            <PhoneInput value={form.mobile} onChange={(v) => setForm(f => ({ ...f, mobile: v }))} testid="client-mobile"/>
                        </Field>
                        <Field label="Email"><Input type="email" value={form.email} onChange={upd("email")} data-testid="client-email-input"/></Field>
                        <Field label="Occupation"><Input value={form.occupation} onChange={upd("occupation")}/></Field>
                        <Field label="District"><Input value={form.district} onChange={upd("district")}/></Field>
                        <Field label="State"><Input value={form.state} onChange={upd("state")}/></Field>
                        <Field label="Pincode"><Input value={form.pincode} onChange={upd("pincode")} data-testid="client-pincode-input" maxLength={6} inputMode="numeric"/></Field>
                    </div>
                    <Field label="Address"><Textarea rows="2" value={form.address} onChange={upd("address")}/></Field>
                    <Field label="Notes"><Textarea rows="3" value={form.notes} onChange={upd("notes")}/></Field>
                    <div className="pt-2 flex justify-end"><GoldButton disabled={saving} data-testid="client-save-btn">{saving ? "Saving…" : "Add client"}</GoldButton></div>
                </form>
            </Modal>

            <Modal open={!!portalFor} onClose={() => setPortalFor(null)} title={`Portal access — ${portalFor?.name || ""}`} testId="portal-access-modal">
                {portalFor && (
                    <form onSubmit={grantPortal} className="space-y-4">
                        {portalAccess?.granted && (
                            <div className="bg-background border border-emerald-700/40 p-3 text-xs text-emerald-300">
                                Currently granted to <span className="font-mono">{portalAccess.email}</span> since {fmtDate(portalAccess.created_at)}.
                                Set a new password below to rotate, or revoke entirely.
                            </div>
                        )}
                        <Field label="Login email" hint="The client will sign in at /login with this email.">
                            <Input required type="email" value={portalForm.email} onChange={e => setPortalForm({ ...portalForm, email: e.target.value })} data-testid="portal-email-input"/>
                        </Field>
                        <Field label="Password">
                            <div className="flex gap-2">
                                <Input required minLength="6" value={portalForm.password} onChange={e => setPortalForm({ ...portalForm, password: e.target.value })} data-testid="portal-password-input"/>
                                <GhostButton type="button" onClick={genPassword} data-testid="portal-gen-password-btn">Generate</GhostButton>
                            </div>
                        </Field>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground">
                            <input type="checkbox" checked={portalForm.send_invite} onChange={e => setPortalForm({ ...portalForm, send_invite: e.target.checked })} data-testid="portal-send-invite"/>
                            Email these credentials to the client (requires platform email integration)
                        </label>
                        <div className="flex items-center justify-between pt-2 gap-2 flex-wrap">
                            {portalAccess?.granted ? (
                                <button type="button" onClick={revokePortal} data-testid="portal-revoke-btn" className="text-xs text-red-400 hover:underline inline-flex items-center gap-1"><ShieldOff className="w-3 h-3" strokeWidth={1.5}/>Revoke access</button>
                            ) : <span></span>}
                            <GoldButton type="submit" data-testid="portal-grant-btn">{portalAccess?.granted ? "Rotate password" : "Grant access"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>
            <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit client — ${edit?._orig?.code || ""}`} size="lg" testId="client-edit-modal">
                {edit && (
                    <form onSubmit={saveEdit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Name"><Input required value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} data-testid="client-edit-name"/></Field>
                            <Field label="Mobile">
                                <PhoneInput value={edit.mobile} onChange={(v) => setEdit({ ...edit, mobile: v })} testid="client-edit-mobile"/>
                            </Field>
                            <Field label="Email"><Input type="email" value={edit.email} onChange={e => setEdit({ ...edit, email: e.target.value })} data-testid="client-edit-email"/></Field>
                            <Field label="Occupation"><Input value={edit.occupation} onChange={e => setEdit({ ...edit, occupation: e.target.value })}/></Field>
                            <Field label="District"><Input value={edit.district} onChange={e => setEdit({ ...edit, district: e.target.value })}/></Field>
                            <Field label="State"><Input value={edit.state} onChange={e => setEdit({ ...edit, state: e.target.value })}/></Field>
                            <Field label="Pincode"><Input value={edit.pincode} onChange={e => setEdit({ ...edit, pincode: e.target.value })} data-testid="client-edit-pincode" maxLength={6} inputMode="numeric"/></Field>
                        </div>
                        <Field label="Address"><Textarea rows="2" value={edit.address} onChange={e => setEdit({ ...edit, address: e.target.value })}/></Field>
                        <Field label="Notes"><Textarea rows="2" value={edit.notes} onChange={e => setEdit({ ...edit, notes: e.target.value })}/></Field>
                        <Field label="Remarks (mandatory · logged to audit trail)" hint="Explain why this edit is being made. Minimum 3 characters.">
                            <Textarea rows="2" value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} data-testid="client-edit-reason" placeholder="e.g. Phone number corrected after client confirmation"/>
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEdit(null)} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
                            <GoldButton type="submit" disabled={editSaving} data-testid="client-edit-save-btn">{editSaving ? "Saving…" : "Save changes"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>

            <HistoryDrawer open={!!historyFor} onClose={() => setHistoryFor(null)} entity="client" entityId={historyFor?.id} title={`${historyFor?.code || ""} · ${historyFor?.name || ""}`} />
            {showBulk && <BulkImportModal onClose={() => setShowBulk(false)} onListRefresh={load} />}
        </>
    );
}